import { color, dark, font, fontWeight, sel } from "@mesketch/design-tokens";

// Logo směr B (DES-003 §4): „me“ Bricolage 800, „sketch“ Bricolage 500, tři tečky stoupající od
// účaří k výšce malých písmen. Barvy SEL jsou jen tady (zadání 15.1).

const XH = 0.5;

/** Tečky loga v souřadnicích 142 × 100. Na tmavém je třetí tečka jen krémový obrys. */
export function LogoDots({ tone = "light" }: { tone?: "light" | "dark" }) {
  const third = tone === "dark" ? sel.cream : color.graphite;
  return (
    <svg viewBox="0 0 142 100" overflow="visible" aria-hidden="true" style={{ height: `${XH}em`, width: `${XH * 1.42}em`, verticalAlign: "baseline", overflow: "visible", marginLeft: "0.07em" }}>
      <circle cx="19" cy="82" r="19" fill={sel.peach} />
      <circle cx="71" cy="50" r="19" fill={sel.teal} />
      <circle cx="123" cy="18" r="16.2" fill={tone === "dark" ? "none" : sel.cream} stroke={third} strokeWidth="6" />
      <ellipse cx="124.4" cy="16.8" rx="17.6" ry="15.8" transform="rotate(-24 124.4 16.8)" fill="none" stroke={third} strokeWidth="3.2" opacity={tone === "dark" ? 0.45 : 0.5} />
    </svg>
  );
}

/** Logo jako text a tečky. Velikost určuje `font-size` rodiče nebo `size` v px. */
export function Logo({ size, tone = "light", label = "mesketch" }: { size?: number; tone?: "light" | "dark"; label?: string }) {
  return (
    <span role="img" aria-label={label} style={{ fontFamily: font.display, whiteSpace: "nowrap", lineHeight: 1, letterSpacing: "-0.02em", fontSize: size === undefined ? undefined : `${size}px` }}>
      <span aria-hidden="true" style={{ fontWeight: fontWeight.heavy, color: tone === "dark" ? dark.text : color.ink }}>
        me
      </span>
      <span aria-hidden="true" style={{ fontWeight: fontWeight.medium, color: tone === "dark" ? dark.secondary : color.graphite }}>
        sketch
      </span>
      <LogoDots tone={tone} />
    </span>
  );
}

/** Ikona aplikace 100 × 100 (DES-003 §4). */
export function AppIcon({ tone = "light", size = 100 }: { tone?: "light" | "dark"; size?: number }) {
  const third = tone === "dark" ? sel.cream : color.graphite;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {tone === "dark" ? <rect width="100" height="100" rx="22.5" fill={dark.background} /> : <rect x=".5" y=".5" width="99" height="99" rx="22.5" fill={color.paper} stroke={color.line} />}
      <circle cx="24" cy="65.6" r="10.2" fill={sel.peach} />
      <circle cx="50" cy="50" r="10.2" fill={sel.teal} />
      <circle cx="76" cy="34.4" r="8.8" fill={tone === "dark" ? "none" : sel.cream} stroke={third} strokeWidth="2.8" />
      <ellipse cx="76.6" cy="33.8" rx="9.6" ry="8.6" transform="rotate(-24 76.6 33.8)" fill="none" stroke={third} strokeWidth="1.5" opacity={tone === "dark" ? 0.45 : 0.5} />
    </svg>
  );
}

/**
 * Maskovatelná ikona PWA [NÁVRH] (DES-003 §4): plocha `paper` přes celý čtverec, tečky světlé
 * ikony uvnitř bezpečné zóny (kruh 80 % šířky), systém si tvar ořízne sám.
 */
export function AppIconMaskable({ size = 512 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="100" height="100" fill={color.paper} />
      <circle cx="24" cy="65.6" r="10.2" fill={sel.peach} />
      <circle cx="50" cy="50" r="10.2" fill={sel.teal} />
      <circle cx="76" cy="34.4" r="8.8" fill={sel.cream} stroke={color.graphite} strokeWidth="2.8" />
      <ellipse cx="76.6" cy="33.8" rx="9.6" ry="8.6" transform="rotate(-24 76.6 33.8)" fill="none" stroke={color.graphite} strokeWidth="1.5" opacity="0.5" />
    </svg>
  );
}

/** Favicon 32 × 32: třetí tečka jen kroužek bez druhého tahu (DES-003 §4). */
export function Favicon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      <rect x=".4" y=".4" width="31.2" height="31.2" rx="7" fill={color.paper} stroke={color.line} strokeWidth=".8" />
      <circle cx="8" cy="20.8" r="3.8" fill={sel.peach} />
      <circle cx="16" cy="16" r="3.8" fill={sel.teal} />
      <circle cx="24" cy="11.2" r="3.1" fill={sel.cream} stroke={color.graphite} strokeWidth="1.5" />
    </svg>
  );
}

/**
 * Animace „Skica se kreslí…“ (DES-003 §4): smyčka 2,8 s, tečky naskočí, třetí se dokreslí tahem.
 * Při `prefers-reduced-motion: reduce` stojí tečky v plném stavu.
 */
export function LoadingDots({ size = 44 }: { size?: number }) {
  const css =
    ".mk-ld circle{transform-box:fill-box;transform-origin:center}" +
    ".mk-ld .a{animation:mk-pa 2.8s infinite}.mk-ld .b{animation:mk-pb 2.8s infinite}.mk-ld .c{stroke-dasharray:100;animation:mk-pc 2.8s infinite}" +
    "@keyframes mk-pa{0%{transform:scale(0)}10%,85%{transform:scale(1)}95%,100%{transform:scale(0)}}" +
    "@keyframes mk-pb{0%,12%{transform:scale(0)}22%,85%{transform:scale(1)}95%,100%{transform:scale(0)}}" +
    "@keyframes mk-pc{0%,24%{stroke-dashoffset:100;fill-opacity:0}50%{stroke-dashoffset:0;fill-opacity:0}60%,85%{stroke-dashoffset:0;fill-opacity:1}95%,100%{stroke-dashoffset:100;fill-opacity:0}}" +
    "@media (prefers-reduced-motion:reduce){.mk-ld circle{animation:none!important}}";
  return (
    <svg className="mk-ld" width={size} height={(size * 31) / 44} viewBox="-4 -4 150 108" aria-hidden="true">
      <style>{css}</style>
      <circle className="a" cx="19" cy="82" r="19" fill={sel.peach} />
      <circle className="b" cx="71" cy="50" r="19" fill={sel.teal} />
      <circle className="c" cx="123" cy="18" r="16.2" pathLength="100" fill={sel.cream} stroke={color.graphite} strokeWidth="6" />
    </svg>
  );
}
